import { MediaCategory, MediaGroup } from "./Prompt";
import { PostKeyphrase, PostReviewEvent, SocialNetworksId } from "..";
import { SocialMentionData } from "@/services/bedrock";

export type SendModeType = "IMMEDIATE" | "SCHEDULED";

export enum SocialNetworkPostStatus {
  PENDING = "PENDING",
  IN_TRANSIT = "IN_TRANSIT",
  DELETED = "DELETED",
  POSTED = "POSTED",
  FAILED = "FAILED",
  FAILURE_ACKED = "FAILURE_ACKED",
  SKIPPED_NO_CONNECTION = "SKIPPED_NO_CONNECTION",
}

export enum PostDeliveryStatus {
  PENDING_PUSH = "PENDING_PUSH",
  FAILED_PUSH = "FAILED_PUSH",
  IGNORE = "IGNORE",
  DELIVERED = "DELIVERED",
}

export enum PostScheduledStatus {
  PENDING = "PENDING",
  POSTED = "POSTED",
  IGNORE = "IGNORE",
  FAILED = "FAILED",
  PENDING_TO_SOCIAL_NETWORK = "PENDING_TO_SOCIAL_NETWORK",
  SKIPPED_NO_CONNECTION = "SKIPPED_NO_CONNECTION",
  PROCESSING = "PROCESSING",
}

export enum SourceType {
  CAMPAIGN = "CAMPAIGN",
  CONTENT_PAGE = "CONTENT_PAGE",
  VIDEO_PROJECT = "VIDEO_PROJECT",
  PROMPT = "PROMPT",
  POST = "POST",
}

export enum ReviewStatus {
  REVIEWED = "REVIEWED",
  COMPLETED = "COMPLETED",
  REQUEST_CHANGES = "REQUEST_CHANGES",
  NULL = "NULL",
  CHANGES_SUBMITTED = "CHANGES_SUBMITTED",
}

export const SOCIAL_NETWORK_POST_STATUS_NAMES: Record<SocialNetworkPostStatus, string> = {
  PENDING: "Scheduled",
  IN_TRANSIT: "Posting In Progress",
  POSTED: "Posted",
  FAILED: "Failed",
  DELETED: "Deleted",
  FAILURE_ACKED: "Failed (Acknowledged)",
  SKIPPED_NO_CONNECTION: "Skipped (Not Connected)",
};

export const POST_REVIEW_STATUS_NAMES = {
  REVIEWED: "Review Completed",
  REQUEST_CHANGES: "Changes Requested",
  COMPLETED: "Review Completed",
  NULL: "In Review",
  CHANGES_SUBMITTED: "In Review",
};

export interface Post {
  postId: number;
  playerId: number;
  promptId?: number;
  videoProjectId?: number;
  message: string;
  mediaGroup?: MediaGroup;
  socialNetworks: SocialNetworkPosted[];
  keyphrases: PostKeyphrase[];
  coachPostReviewStatus?: ReviewStatus;
  coachPostReviewStatusUpdatedDate: string;
  postReviewRequestedByFullName?: string;
  playerBranch?: string;
  playerRegion?: string;
  postReviewRequestedBy?: number;
  shortLink?: string;
  hashtags?: string;
  complianceDisclaimer?: string;
  postReviewEvents?: PostReviewEvent[];
  canBeUpdatedOrDeleted?: boolean;
  deliveryStatus?: PostDeliveryStatus;
  deliveryDatetimeUtc?: string;
  scheduledStatus?: PostScheduledStatus;
  scheduledDatetimeUtc?: string;
}

export interface ComplianceExportEntry {
  postId: number;
  postSocialNetworkId: number;
  socialNetworkId: SocialNetworksId;
  playerId: number;
  sourceType?: SourceType;
  sourceId?: number;
  status: SocialNetworkPostStatus;
  postedTimestamp?: string;
  externalPostUrl?: string;
  caption?: string;
  mediaCategory?: MediaCategory;
  mediaUri?: string;
  mediaTranscript?: string;
  keyphrases?: string;
  reviewStatus?: ReviewStatus;
  reviewedStatusDate?: string;
  reviewedById?: number;
}

export interface SocialNetworkMessage {
  code: string;
  socialNetworkMessage?: string;
  postedCaption?: string;
  socialNetworkId?: SocialNetworksId;
  failedReason?: string;
  status?: string;
  postedTimestamp?: string;
  createdDate?: string;
  posted?: boolean;
  externalPostUrl?: string;
  caption?: string;
  mentions?: SocialMentionData[];
}

export interface BasePostRequest {
  messageToPost: string;
  sendMode: SendModeType;
  scheduledDatetimeUtc?: string;
  socialNetworkMessages: SocialNetworkMessage[];
  isAutoPost: boolean;
  mediaGroup?: MediaGroup;
}

export interface PostRequest extends BasePostRequest {
  coachId: number;
  playerIds: number[];
  promptId: number | string;
  brandId?: string;
  allowHashtags?: boolean;
  allowComplianceDisclaimer?: boolean;
  allowShortLink?: boolean;
}

export interface PostPlayerRequest extends BasePostRequest {
  playerId?: number;
  promptId?: number | string;
  postId?: number | string;
  mediaGroup?: MediaGroup;
}

export interface SocialNetworkPosted {
  postSocialNetworkId: number;
  socialNetworkId: SocialNetworksId;
  status: SocialNetworkPostStatus;
  caption?: string;
  postedTimestamp: string;
  externalPostUrl?: string;
  secondaryExternalPostUrl?: string;
  failedReason?: string;
}

export interface MessageChat {
  type: "LEFT" | "RIGHT";
  owner?: string;
  date?: string;
  message: string;
}
